import {Component, Input, Output, EventEmitter, ChangeDetectorRef, OnInit, OnChanges} from "@angular/core";
import {ISelection, ISentence, IQuestion, IAnswer,
        IHint, IQuestionEntry, QuestionType} from  "../api/data-model";
import {Subscription} from "rxjs";
import {ContentCursor} from "../services/content/content.cursor";
import {BugsnagLoggingService} from "../services/logging/bugsnag-logging.service";
import {CssSentence} from "../content/content-view.objects";
import {UtilitiesService} from "../services/utilities.service";
import {AudioState, Scilearn} from "../constants";
import {QuestionPipelineService} from "../services/pipeline/question.pipeline.service";

@Component({
    selector: "question-editor-detail",
    templateUrl: "./question.editor.detail.component.html",
    styleUrls: ["./pipeline.component.scss"]
})
export class QuestionEditorDetailComponent implements OnInit, OnChanges {

    @Input()
    public question:IQuestionEntry;

    @Input()
    public selection: ISelection;

    @Input()
    public _activated: boolean = false;

    @Input()
    public idx:string;

    @Output()
    public saveRequest:EventEmitter<IQuestionEntry> = new EventEmitter<IQuestionEntry>();

    @Output()
    public deleteRequest:EventEmitter<IQuestionEntry> = new EventEmitter<IQuestionEntry>();

    @Output()
    public setActiveRequest:EventEmitter<string> = new EventEmitter<string>();

    public originalQuestion: IQuestionEntry;

    public editingSentence:boolean = false;

    public selectedSentence:CssSentence = null;

    public taiSentence:ISentence = null;

    public openBookSentence: ISentence = null;

    public addInProgress: boolean = false;

    public showAddAnswer: boolean = false;

    public showAddHint: boolean = false;

    public newHint: IHint;

    public skillValues: string[] = QuestionPipelineService.skillValues;

    public thinkingValues: string[] = QuestionPipelineService.thinkingValues;

    public dropdownDefault = "---Select---";

    private scSubscription: Subscription;

    constructor ( private contentCursor: ContentCursor,
                  private logService: BugsnagLoggingService,
                  private ref: ChangeDetectorRef
    ) {

    }

    ngOnInit(): void {
        console.log("initializing question");
        if (this.question.presentation === Scilearn.THINK_ABOUT_IT) {
            this.getTAISentenceFromQuestion();
        }
        if (this.question.type === "OpenBook" && this.question.answers && this.question.answers.length > 0) {
            this.findOpenBookSentence(this.question.answers[0].answerSentenceId);
        }
        this.originalQuestion = JSON.parse(JSON.stringify(this.question));
    }

    ngOnChanges(changes):void {
        if (changes._activated) {
            if (changes._activated.currentValue === true) {
                console.log("adding subscriber for question " + this.idx);
                this.initPresentationSubscriber();
            } else {
                console.log("clearing subscriber for question " + this.idx);
                this.clearSubscriber();
            }
        }
    }

    public showAddNewAnswer():void {
        this.question.answers.push(new NewAnswer(this.question.type === Scilearn.QUESTIONTYPE_OPENBOOK));
        this.ref.detectChanges();
    }

    public deleteAnswer(index:number):void {
        if (confirm("Are you sure you want to delete this?") === true) {
            this.question.answers.splice(index, 1);
            // TODO:  Why do I need to do this?? It will not update the UI, otherwise.
            this.ref.detectChanges();
        }
    }

    public checkValidQuestion(): void {
        // check that required data is there and self-consistent
        let errorMessage = "";
        errorMessage += this.checkQuestionData();
        errorMessage += this.checkAnswerData();
        errorMessage += this.checkConsistency();
        if (errorMessage === "") {
            errorMessage = "no errors in question";
        }
        alert(errorMessage);
    }

    private checkQuestionData(): string {
        let errorMessage = "";
        console.log("skill: " + this.question.skill);
        if ((this.question.skill === this.dropdownDefault) || (!this.question.skill) ||
            (this.question.skill === "")) {
            errorMessage += "missing skill\n";
        }
        if ((this.question.thinking === this.dropdownDefault) || (!this.question.thinking) ||
            (this.question.thinking === "")) {
            errorMessage += "missing level of thinking\n";
        }
        if (this.question.question.text === "" || this.question.question.text === "Add a new one.") {
            errorMessage += "missing or invalid question text\n";
        }
        if (!([Scilearn.QUESTIONTYPE_MULTICHOICE, Scilearn.QUESTIONTYPE_MULTICHECKS, Scilearn.QUESTIONTYPE_PROMPT,
                 Scilearn.QUESTIONTYPE_OPENBOOK, Scilearn.QUESTIONTYPE_TRUEFALSE].includes(this.question.type))) {
            errorMessage += "missing question type\n";
        }
        if (!([Scilearn.THINK_ABOUT_IT, Scilearn.QUIZ_QUESTION].includes(this.question.presentation))) {
            errorMessage += "missing question presentation point (TAI or Quiz)\n";
        }
        if (this.question.presentation === Scilearn.THINK_ABOUT_IT) {
            let taiAnchors = this.getTAISentenceFromQuestion();
            if (taiAnchors.length !== 1) {
                errorMessage += taiAnchors.length === 0 ? "missing TAI anchor sentence\n" :
                                "multiple TAI anchor sentences for this question\n";
            }
        }
        return errorMessage;
    }

    private checkAnswerData(): string {
        let errorMessage = "";
        // Open Book questions have one answer (the id of a sentence in the selection)
        // True/false have two; Prompts are not actually questions, and have no answers.
        // Other types must have at least two answer alternatives to choose from
        let actualNumAnswers = this.question.answers ? this.question.answers.length : 0;
        let hasRequiredNumAnswers;
        switch (this.question.type) {
            case Scilearn.QUESTIONTYPE_PROMPT:
                hasRequiredNumAnswers = actualNumAnswers === 0;
                break;
            case Scilearn.QUESTIONTYPE_OPENBOOK:
                hasRequiredNumAnswers = actualNumAnswers === 1;
                break;
            case Scilearn.QUESTIONTYPE_TRUEFALSE:
                hasRequiredNumAnswers = actualNumAnswers === 2;
                break;
            case Scilearn.QUESTIONTYPE_MULTICHOICE:
            case Scilearn.QUESTIONTYPE_MULTICHECKS:
                hasRequiredNumAnswers = actualNumAnswers >= 2;
                break;
            default:
                hasRequiredNumAnswers = false;
        }
        if (!hasRequiredNumAnswers) {
            errorMessage += "question type: " + this.question.type + " is inconsistent with number of answers:" +
                            this.question.answers.length.toString() + "\n";
        }
        // check that answers have text except for openbook, which has a sentence id as the answer.
        if (this.question.type !== Scilearn.QUESTIONTYPE_OPENBOOK) {
            let emptyAnswers = [];
            if (this.question.answers) {
                emptyAnswers = this.question.answers.filter((answer) => {
                    return answer.text === "";
                });
            }
            if (emptyAnswers.length > 0) {
                errorMessage += "one or more answers is missing text\n";
            }
        } else { // if it is openbook, need an answer sentence number
            if (!this.question.answers || this.question.answers.length === 0 ||
                !this.question.answers[0].answerSentenceId.match(/^\d+$/)) {
                errorMessage += "OpenBook question is missing answer sentence\n";
            }
        }
        return errorMessage;
    }

    private checkConsistency(): string {
        // check for internal consistency of data:

        // 1) true false type has true/false answers
        let errorMessage = "";
        let expectedAnswers = ["false", "true"].toString();
        let answerTexts = this.question.answers.map((answer) => {
            return answer.text.toLowerCase();
        });
        if (this.question.type === Scilearn.QUESTIONTYPE_TRUEFALSE &&
            answerTexts.sort().toString() !== expectedAnswers) {
            errorMessage += "TrueFalse answers are not true and false\n";
        }

        // 2) number of correct answers is consistent with question type
        if (this.question.type !== Scilearn.QUESTIONTYPE_PROMPT) { // prompts have no answers (already checked above)
            let numCorrect = this.question.answers.filter((answer) => {
                    // legacy v1 content has correct values as string "true" rather than boolean
                    return answer.correct === true || answer.correct as unknown as string === "true";
                }).length;
            if (this.question.type === Scilearn.QUESTIONTYPE_MULTICHECKS) {
                // not 100% certain to be an error, but the multiple correct answer type (MultipleChecks) typically has
                // more than one correct answer
                if (numCorrect < 2) {
                    errorMessage += "expected more than one correct answer for Multiple Correct type (may be ok?)\n";
                }
            } else {
                // otherwise, all other non-prompt types should have exactly one correct answer
                if (numCorrect !== 1) {
                    errorMessage += "expected one correct answer for type:" + this.question.type + " but got: " +
                                    numCorrect.toString() + "\n";
                }
            }
        }
        // 3) question type is consistent which whether question is Quiz or TAI: OpenBook and Prompt are never in Quiz
        if (this.question.presentation === Scilearn.QUIZ_QUESTION &&
            [Scilearn.QUESTIONTYPE_OPENBOOK, Scilearn.QUESTIONTYPE_PROMPT].includes(this.question.type)) {
            errorMessage += "question type: " + this.question.type + " should not occur as a quiz question\n";
        }
        return errorMessage;
    }

    public save():void {
        let doSave = true;
        if (this.flagAudioTextMismatch()) {
            if (confirm("This question has audio scripted or recorded which now may not match edited text (it will be marked as needing re-recording). Are you sure?")
                !== true) {
                doSave = false;
            }
        }
        if (doSave) {
            this.doSmartQuotes(this.question);
            this.originalQuestion = JSON.parse(JSON.stringify(this.question));
            this.saveRequest.emit(this.question);
        } else { // not saving, so revert to original question entry
            this.question = JSON.parse(JSON.stringify(this.originalQuestion));
            this.ref.detectChanges();
        }
    }

    private doSmartQuotes(question: IQuestionEntry): void {
        question.question.text = UtilitiesService.smarten(question.question.text);
        this.question.answers.forEach((answer: IAnswer) => {
            answer.text = UtilitiesService.smarten(answer.text);
        });
    }

    private flagAudioTextMismatch(): boolean {
        let flagMismatch = false;
        if (this.question.question.audio && (this.question.question.text !== this.originalQuestion.question.text)) {
            flagMismatch = true;
            if (this.question.question.audioState !== AudioState.UNRECORDED) {
                this.question.question.audioState = AudioState.RERECORD;
            }
        }
        this.question.answers.forEach((answer: IAnswer, index: number) => {
            if (answer.audio && (answer.text !== this.originalQuestion.answers[index].text)) {
                flagMismatch = true;
                if (answer.audioState !== AudioState.UNRECORDED) {
                    answer.audioState = AudioState.RERECORD;
                }
            }
        });
        return flagMismatch;
    }

    public deleteQuestion():void {
        if (this.question.presentation === Scilearn.THINK_ABOUT_IT) {
            this.clearTAISentence();
        }
        if (this.question.type === "OpenBook") {
            this.openBookSentence = null;
        }
        this.deleteRequest.emit(this.question);
    }

    public changeType(type: QuestionType):void {
        if (this.question.type === type) {
            return;
        }
        // True/False questions should always have exactly 2 answers.  We do not require the answers be 'true' and
        // 'false' here since we may wish to include other binary answers (e.g. yes/no) in this type
        let numTrueFalseChoices = 2;
        let requireDelete = (type === "OpenBook") || (type === "Prompt") ||
                            (type === "TrueFalse" && this.question.answers.length !== numTrueFalseChoices);
        if (this.question.answers.length > 0 && requireDelete) {
            if (confirm("Changing question type will delete existing answers.  Are you sure?") === true) {
                this.question.answers = [];
                this.question.type = type;
                this.openBookSentence = null;
            }
        } else {
            this.question.type = type;
            this.openBookSentence = null;
        }
    }

    public changePresentation(presentationType:string):void {
        this.question.presentation = presentationType;
        if (presentationType === Scilearn.THINK_ABOUT_IT) {
            // will set subscriber for TAI
            this.finalizeOpenBookSentence();
        } else {
            this.clearSubscriber();
            this.clearTAISentence();
        }
    }

    public initPresentationSubscriber(): void {
        this.scSubscription = this.contentCursor.sentenceChanged.subscribe(sentence => {
            if (this.question.presentation === Scilearn.THINK_ABOUT_IT) {
                this.selectedSentence = sentence;
                this.logService.log("TAI selected sentence:" + sentence);
                this.clearTAISentence();
                let taiSentence = this.findTAISentence();
                taiSentence.words[0].startQuestionId = this.question.id;
                taiSentence.words[taiSentence.words.length - 1].questionId = this.question.id;
                this.logService.log("TAI modified sentence:" + taiSentence);
                this.getTAISentenceFromQuestion();
            }
        });
    }

    public getTAISentenceFromQuestion(): ISentence[] {
        // console.log(JSON.stringify(this.selection));
        this.taiSentence = null;
        let taiSentences:ISentence[] = [];
        for (let text of this.selection.text) {
            if (text && text.sentences) {
                for (let sentence of text.sentences) {
                    if (sentence && sentence.words) {
                        if (sentence.words[0].startQuestionId === this.question.id) {
                            console.log("foundTAI in sentence starting with " + sentence.words[0].word
                                        + " and ending with " + sentence.words[sentence.words.length - 1].word);
                            this.taiSentence = sentence;
                            taiSentences.push(sentence);
                        }
                    }
                }
            }
        }
        if (this.taiSentence) {
            console.log("taiSentence ends with: " + this.taiSentence.words[this.taiSentence.words.length - 1].word);
        } else {
            console.log("taiSentence is null");
        }
        return taiSentences;
    }

    public clearTAISentence():ISentence {
        for (let text of this.selection.text) {
            if (text && text.sentences) {
                for (let sentence of text.sentences) {
                    if (sentence && sentence.words) {
                        if (sentence.words[0].startQuestionId === this.question.id) {
                            sentence.words[0].startQuestionId = "";
                            sentence.words[sentence.words.length - 1].questionId = "";
                        }
                    }
                }
            }
        }
        this.taiSentence = null;
        return null;
    }

    public findTAISentence():ISentence {
        return this.findSentenceById(this.selectedSentence.id);
    }

    public findOpenBookSentence(id:string):void {
        this.openBookSentence = this.findSentenceById(id);
    }

    public findSentenceById(id:string):ISentence {
        for (let text of this.selection.text) {
            if (text && text.sentences) {
                for (let sentence of text.sentences) {
                    if (sentence && sentence.id === id) {
                        return sentence;
                    }
                }
            }
        }
        return null;
    }

    initOpenBookSubscriber(): void {
        this.clearSubscriber();
        this.scSubscription = this.contentCursor.sentenceChanged.subscribe(sentence => {
            this.selectedSentence = sentence;
            if (this.question && (this.question.answers.length > 0)) {
                this.question.answers[0].answerSentenceId = sentence.id;
            }
            this.findOpenBookSentence(sentence.id);
            this.logService.log("question answer sentence Id:" + sentence.id);
        });
    }

    closeOpenBookSubscriber(): void {
        this.clearSubscriber();
        if (this.question.presentation === Scilearn.THINK_ABOUT_IT) {
            this.initPresentationSubscriber();
        }
    }

    editOpenBookSentence(): void {
        this.editingSentence = true;
        this.contentCursor.clearSelection();
        this.selectedSentence = null;
        if (this.question) {
            if (this.question.answers.length > 0) {
                this.findOpenBookSentence(this.question.answers[0].answerSentenceId);
            } else {
                this.question.answers.push(new NewAnswer(true));
            }
            this.question.answers[0].correct = true;
        }
        this.initOpenBookSubscriber();
    }

    finalizeOpenBookSentence(): void {
        this.editingSentence = false;
        this.closeOpenBookSubscriber();
    }

    public clearSubscriber(): void {
        if (this.scSubscription) {
            this.scSubscription.unsubscribe();
        }
    }

    public toggleCard(): void {
        // this._activated = !this._activated;
        if (!this._activated) {
            console.log("Activating card " + this.idx);
            this.setActiveRequest.emit(this.idx);
        } else {
            console.log("De-activating card " + this.idx);
            this.setActiveRequest.emit("-1");
        }
    }

    public ngOnDestroy(): void {
        console.log("destroying question editor component and unsubscribing multi-use listener");
        if (this.scSubscription) {
            this.scSubscription.unsubscribe();
        }
    }

}

export class NewAnswer implements IAnswer {
    constructor(isOpenBook: boolean) {
        this.correct = false;
        this.audio = "";
        if (!isOpenBook) {
            this.audioState = AudioState.UNRECORDED;
        }
        this.text = "";
    }
    correct: boolean;
    answerSentenceId: string;
    audio: string;
    audioState?: string;
    text: string;
    hints: IHint[];
}
